import { ReactNode, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { rgba } from 'polished';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Disclosure } from '@components/disclosure';
import { IconAppExtraSmall } from '@components/icons/app-extra-small';
import { IconAttachment } from '@components/icons/attachment';
import { IconFlagGB } from '@components/icons/flag-gb';
import { IconFlagIS } from '@components/icons/flag-is';
import { IconForm } from '@components/icons/form';
import { IconList } from '@components/icons/list';
import { IconQuestion } from '@components/icons/question';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { JobLabel } from 'api/labels/requests';
import { Box } from 'components/Box';
import { useStore } from 'models/Provider';
import { IJob, IJobFormSection } from 'models/types';

export function Details({ job }: { job: IJob }) {
  const { t } = useStore();
  const [language, setLanguage] = useState(job.defaultLanguageData?.language ?? 'en');

  return (
    <Stack space={6}>
      <Box flex justifyContent="space-between">
        <Box flex h="40" alignItems="center">
          <Text font="bold18" text={t('job.info.details.header')} />
        </Box>
        {job.languages.length > 1 && (
          <Box w="200px">
            <Select
              onChange={(value) => setLanguage(value)}
              options={[
                {
                  value: 'en',
                  label: (
                    <Box flex alignItems="center">
                      <Box flex alignItems="center" mr="8">
                        <IconFlagGB />
                      </Box>
                      {t('job.info.details.language.english')}
                    </Box>
                  ),
                },
                {
                  value: 'is',
                  label: (
                    <Box flex alignItems="center">
                      <Box flex alignItems="center" mr="8">
                        <IconFlagIS />
                      </Box>
                      {t('job.info.details.language.icelandic')}
                    </Box>
                  ),
                },
              ]}
              defaultValue="en"
              isClearable={false}
            />
          </Box>
        )}
      </Box>
      <Separator />
      <Disclosure
        icon={<IconList color="purple80" />}
        text={t('job.info.details.jobdetails.header')}
        content={[<JobDetailsContent job={job} language={language} />]}
      />
      <Separator />
      <Disclosure
        icon={<IconTextLong color="purple80" />}
        text={t('job.info.details.jobdescription.header')}
        content={[<JobDescriptionContent job={job} language={language} />]}
      />
      <Separator />
      <Disclosure
        icon={<IconForm color="purple80" />}
        text={t('job.info.details.applicationform.header')}
        content={[<ApplicationFormContent job={job} />]}
      />
      <Separator />
      <Disclosure
        icon={<IconQuestion color="purple80" />}
        text={t('job.info.details.questions.header')}
        content={[<QuestionsContent job={job} language={language} />]}
      />
      <Separator />
    </Stack>
  );
}

interface JobProps {
  job: IJob;
}
interface JobAndLanguageProps {
  job: IJob;
  language: string;
}

const JobDetailsContent = observer((props: JobAndLanguageProps) => {
  const { current, t } = useStore();
  const { job, language } = props;
  const selectedLanguage = job.languages.find((job: IJob) => job.language === language);
  return (
    <Columns>
      <Field label={t('job.info.details.jobdetails.jobtitle')} value={selectedLanguage?.title} />
      <Field
        label={t('job.info.details.jobdetails.worktype')}
        value={job.workTypeDisplay(language)}
      />
      <Field
        label={t('job.info.details.jobdetails.location')}
        value={job.locationDisplay(language)}
      />
      {current.info.isEnterprise && (
        <Field
          label={t('job.info.details.jobdetails.department')}
          value={job.departmentDisplay(language)}
        />
      )}
      <Field
        label={t('job.info.details.jobdetails.jobstatus')}
        value={t(`job.status.${job.status}`)}
      />
      <Field
        label={t('job.info.details.jobdetails.visibility')}
        value={t(`job.visibility.${job.visibility}`)}
      />
      <Field label={t('job.info.details.jobdetails.deadline')} value={job.deadlineDisplay} />
      <Field
        label={t('job.info.details.jobdetails.published')}
        value={job.datePublishedDisplayWithoutPrefix}
      />
      <Field
        label={t('job.info.details.jobdetails.createdby')}
        value={job.createdById && job.createdById.name}
      />
      {job.labelsDisplay.length > 0 ? (
        <Stack space={1}>
          <Text font="semibold14" text={t('job.info.details.jobdetails.labels')} />
          <LabelColumns>
            {job.labelsDisplay.map((label: JobLabel) => (
              <Badge
                key={label.id}
                style={{
                  color: label.color,
                  backgroundColor: rgba(label.color, 0.1),
                }}
                icon={<IconAppExtraSmall />}
                text={label.title}
              />
            ))}
          </LabelColumns>
        </Stack>
      ) : null}
    </Columns>
  );
});

const LabelColumns = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  rowGap: pxToRem(8),
  div: {
    gridColumnStart: 'span 3',
  },
});

const JobDescriptionContent = observer((props: JobAndLanguageProps) => {
  const { t } = useStore();
  const { job, language } = props;
  const selectedLanguage = job.languages.find((job: IJob) => job.language === language);
  return (
    <Stack space={4}>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdescription.shortdescription')} />
        <RichText dangerouslySetInnerHTML={{ __html: selectedLanguage?.shortDescriptionHtml }} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdescription.longdescription')} />
        <RichText dangerouslySetInnerHTML={{ __html: selectedLanguage?.description }} />
      </div>
    </Stack>
  );
});

const RichText = styled.div({
  font: theme.font.regular14,
  color: theme.color.textPrimary,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  '& > *:first-child': { marginTop: 0 },
  '& > *:last-child': { marginBottom: 0 },
});

const ApplicationFormContent = observer((props: JobProps) => {
  const { t } = useStore();
  const { job } = props;

  const getRequirementDisplay = (
    isRequired: boolean,
    isIncluded: boolean
  ): { color: Color; value: string } => {
    if (isRequired) {
      return {
        color: 'green100',
        value: t('job.info.details.applicationform.options.required'),
      };
    }
    if (isIncluded) {
      return {
        color: 'orange100',
        value: t('job.info.details.applicationform.options.optional'),
      };
    }
    return {
      color: 'purple60',
      value: t('job.info.details.applicationform.options.off'),
    };
  };

  return (
    <Columns>
      <Field
        label={t('job.info.details.applicationform.name')}
        value={t('job.info.details.applicationform.options.alwaysrequired')}
        color="blue100"
      />
      <Field
        label={t('job.info.details.applicationform.email')}
        value={t('job.info.details.applicationform.options.alwaysrequired')}
        color="blue100"
      />
      <Field
        label={t('job.info.details.applicationform.ssn')}
        {...getRequirementDisplay(job.requireSsn, job.includeSsn)}
      />
      <Field
        label={t('job.info.details.applicationform.phone')}
        {...getRequirementDisplay(job.requirePhone, job.includePhone)}
      />
      <Field
        label={t('job.info.details.applicationform.cv')}
        {...getRequirementDisplay(job.requireCv, job.includeCv)}
      />
      <Field
        label={t('job.info.details.applicationform.coverletter')}
        {...getRequirementDisplay(job.requireCoverLetter, job.includeCoverLetter)}
      />
    </Columns>
  );
});

const QuestionsContent = observer((props: JobAndLanguageProps) => {
  const { t } = useStore();
  const { job, language } = props;
  const sections = job.languages.find((job: IJob) => job.language === language)?.form?.sections;
  return (
    <div>
      {sections &&
        sections.map((section: IJobFormSection) =>
          section.questions.map((question) => {
            return (
              <Box alignItems="center" key={question.id} mb="16">
                <Box flex alignItems="center">
                  <Stack space={1} orientation="horizontal">
                    {questionIcon({ type: question.type })}
                    <Text font="semibold14" text={question.title} />
                  </Stack>
                </Box>
                <Text
                  font="regular14"
                  color={
                    question.required ? 'green100' : question.hidden ? 'purple60' : 'orange100'
                  }
                  text={
                    question.required
                      ? t('job.info.details.applicationform.options.required')
                      : question.hidden
                      ? t('job.info.details.applicationform.options.off')
                      : t('job.info.details.applicationform.options.optional')
                  }
                />
              </Box>
            );
          })
        )}
    </div>
  );
});

function questionIcon({ type }: { type: string }) {
  switch (type) {
    case 'multi_line':
      return <IconTextLong color="purple100" />;
    case 'dropdown':
      return <IconSelectSingle color="purple100" />;
    case 'multidropdown':
      return <IconSelectMultiple color="purple100" />;
    case 'attachment':
      return <IconAttachment color="purple100" />;
    case 'one_line':
    default:
      return <IconTextShort color="purple100" />;
  }
}

const Columns = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  rowGap: '1rem',
  columnGap: '1.5rem',
  div: {
    gridColumnStart: 'span 3',
  },
});

type FieldProps = {
  label: string;
  value?: ReactNode;
  color?: Color;
};

const Field = ({ label, value, color }: FieldProps) => {
  return (
    <div>
      <Text font="semibold14" text={label} />
      <Text font="regular14" color={color} text={value} />
    </div>
  );
};
